<template>
  <AppNavbar />
  <div class="vx-admin">

    <div class="mb-3">
      <div class="vx-migas mb-1"><span>Suscripciones</span><span class="sep">/</span><span>Cargos eventuales</span></div>
      <h1 class="vx-titulo">Cargos eventuales</h1>
      <div class="vx-ayuda">
        Un cobro que no estaba previsto al armar el esquema —un test psicológico, una
        revisión médica, una salida—. Se define acá y se le emite
        <strong>sólo a quien corresponda</strong>.
      </div>
    </div>

    <!-- Paso 1: de qué esquema -->
    <div class="vx-panel mb-3">
      <label class="vx-etiqueta" for="esquema">Esquema de cobro</label>
      <select id="esquema" v-model="esquemaId" class="vx-campo" data-test="select-esquema"
              @change="cambiarEsquema">
        <option :value="null" disabled>Elegí un esquema…</option>
        <option v-for="e in esquemas" :key="e.cr_esquema_id" :value="e.cr_esquema_id">
          {{ e.nombre }}
        </option>
      </select>
      <div class="vx-ayuda mt-1">
        El cargo vive dentro de un esquema, como la cuota. Sólo se le puede emitir a
        suscripciones de ese esquema.
      </div>
    </div>

    <template v-if="esquemaId">
      <!-- Paso 2: los cargos definidos -->
      <div class="vx-panel mb-3">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <h2 class="vx-subtitulo mb-0">Cargos definidos</h2>
          <button class="vx-btn vx-btn-sm" data-test="btn-nuevo-cargo"
                  @click="mostrarAlta = !mostrarAlta">
            <i class="bi" :class="mostrarAlta ? 'bi-x-lg' : 'bi-plus-lg'"></i>
            {{ mostrarAlta ? 'Cancelar' : 'Nuevo cargo' }}
          </button>
        </div>

        <div v-if="mostrarAlta" class="vx-subpanel mb-3" data-test="form-cargo">
          <div class="row g-2">
            <div class="col-md-5">
              <label class="vx-etiqueta" for="c-desc">Descripción</label>
              <!-- Placeholder genérico a propósito (artículo 2): un ejemplo del
                   rubro educativo acá le arma el modelo mental a cualquier otro
                   cliente. Los ejemplos van en la ayuda de arriba, que se lee
                   como ejemplo y no como sugerencia de contenido. -->
              <input id="c-desc" v-model="nuevo.descripcion" class="vx-campo vx-campo-sm"
                     placeholder="Nombre del cargo">
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="c-monto">Monto</label>
              <input id="c-monto" v-model.number="nuevo.monto" type="number" min="0"
                     class="vx-campo vx-campo-sm vx-num">
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="c-iva">IVA</label>
              <select id="c-iva" v-model.number="nuevo.tipo_iva" class="vx-campo vx-campo-sm">
                <option v-for="t in TIPOS_IVA" :key="t.valor" :value="t.valor">{{ t.texto }}</option>
              </select>
            </div>
            <div class="col-md-1 d-flex align-items-end">
              <button class="vx-btn vx-btn-primario vx-btn-sm w-100" data-test="btn-guardar-cargo"
                      :disabled="guardando" @click="guardar">
                Guardar
              </button>
            </div>
          </div>
          <div class="vx-ayuda mt-2">
            Definir no cobra nada. El cargo queda disponible para emitirlo cuando haga falta.
          </div>
        </div>

        <div v-if="!cargos.length" class="vx-vacio">
          <span class="vx-vacio-texto">Este esquema no tiene cargos eventuales.</span>
        </div>

        <table v-else class="vx-tabla" data-test="tabla-cargos">
          <thead>
            <tr>
              <th>Cargo</th><th>IVA</th><th class="vx-col-num">Monto</th>
              <th class="vx-col-num">Emitidos</th><th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="c in cargos" :key="c.cr_esquema_concepto_id">
            <tr :class="{ 'vx-fila-inactiva': !c.activo, 'vx-fila-activa': c.cr_esquema_concepto_id === cargoId }">
              <td>
                <div class="vx-celda-principal">{{ c.descripcion }}</div>
                <div v-if="!c.activo" class="vx-celda-sub">Desactivado</div>
              </td>
              <td class="vx-ayuda">{{ ivaTexto(c.tipo_iva) }}</td>
              <td class="vx-col-num vx-num">{{ formatearMonto(c.monto, moneda) }}</td>
              <td class="vx-col-num vx-num">{{ c.emitidas }}</td>
              <td class="text-end">
                <div class="d-inline-flex gap-1 align-items-center">
                  <button class="vx-btn vx-btn-sm" data-test="btn-emitir"
                          :disabled="!c.activo" @click="abrirEmision(c)">
                    <i class="bi bi-send"></i> Emitir
                  </button>
                  <!-- D45 / RN-163: editar ya no se apaga al emitir. Recalcula
                       lo emitido que nadie pagó; lo cobrado no se toca. Las
                       acciones van en el mismo menú ⋮ que en el esquema. -->
                  <ConceptoAccionesMenu
                    :concepto="c" :puede-cargos="true" @accion="alElegirAccion"
                  />
                </div>
              </td>
            </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Paso 3: a quién -->
      <div v-if="cargoId" class="vx-panel" data-test="panel-emision">
        <h2 class="vx-subtitulo">Emitir «{{ cargoActual?.descripcion }}»</h2>

        <!-- Deshacer una emision — RN-65.
             El motivo NO es decorativo: la base lo exige
             (`cr_deudas_anulacion_justificada`) y el servicio devuelve 422 sin
             el. Articulo 6: el operador decide, con actor y motivo. -->
        <div v-if="quitando" class="vx-subpanel mb-3" data-test="panel-quitar">
          <div class="vx-celda-principal mb-1">
            Quitarle «{{ cargoActual?.descripcion }}» a
            <strong>{{ quitando.nombre }}</strong>
          </div>
          <div class="vx-ayuda mb-2">
            Se anula la deuda que se le emitio. No se borra: queda anulada, con
            motivo y autor, y el cargo se le puede volver a emitir.
          </div>
          <div class="row g-2 align-items-end">
            <div class="col-md-8">
              <label class="vx-etiqueta" for="q-motivo">Motivo</label>
              <input id="q-motivo" v-model="motivoQuitar" class="vx-campo vx-campo-sm"
                     placeholder="Por que se quita" data-test="input-motivo-quitar">
            </div>
            <div class="col-md-4 d-flex gap-2">
              <button class="vx-btn vx-btn-primario vx-btn-sm" data-test="btn-confirmar-quitar"
                      :disabled="ocupado || !motivoQuitar.trim()" @click="confirmarQuitar">
                Quitar el cargo
              </button>
              <button class="vx-btn vx-btn-sm" data-test="btn-cancelar-quitar"
                      :disabled="ocupado" @click="quitando = null">
                Cancelar
              </button>
            </div>
          </div>
        </div>

        <div class="row g-2 align-items-end mb-3">
          <div class="col-md-3">
            <label class="vx-etiqueta" for="venc">Vence el</label>
            <input id="venc" v-model="fechaVencimiento" type="date" class="vx-campo vx-campo-sm"
                   :min="hoy" data-test="input-vencimiento">
            <div class="vx-ayuda">No puede ser anterior a hoy: nacería vencida.</div>
          </div>
          <div class="col-md-9 d-flex gap-2 justify-content-end">
            <button class="vx-btn vx-btn-sm" data-test="btn-todos" @click="marcarTodos(true)">
              Marcar todos
            </button>
            <button class="vx-btn vx-btn-sm" data-test="btn-ninguno" @click="marcarTodos(false)">
              Ninguno
            </button>
          </div>
        </div>

        <div v-if="!elegibles.length" class="vx-vacio">
          <span class="vx-vacio-texto">Este esquema no tiene suscripciones activas.</span>
        </div>

        <table v-else class="vx-tabla" data-test="tabla-elegibles">
          <thead>
            <tr>
              <th style="width: 40px"></th>
              <th>Quién</th><th>Responsable de pago</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in elegibles" :key="s.cr_suscripcion_id">
              <td>
                <input type="checkbox" class="form-check-input"
                       :checked="elegidas.has(s.cr_suscripcion_id)"
                       :disabled="s.ya_lo_tiene || s.sin_responsable"
                       :data-test="`chk-${s.cr_suscripcion_id}`"
                       @change="alternar(s)">
              </td>
              <td>
                <div class="vx-celda-principal">{{ s.ficha?.cabecera?.nombre || '—' }}</div>
                <div class="vx-celda-sub vx-num">{{ s.ficha?.cabecera?.id || '' }}</div>
              </td>
              <td class="vx-ayuda">{{ s.responsable_nombre || '—' }}</td>
              <td class="text-end">
                <!-- No se ocultan: el operador tiene que VER por qué no puede
                     elegirlos, no que desaparezcan sin explicación. -->
                <div v-if="s.ya_lo_tiene" class="d-inline-flex align-items-center gap-2">
                  <span class="vx-estado vx-estado-neutro">Ya lo tiene</span>
                  <!-- La operacion inversa de emitir (RN-65). Se ofrece SOLO si
                       la deuda no tiene pago aplicado: con un pago encima lo que
                       se anula es el COBRO (RN-27), porque es lo que revierte la
                       plata y resuelve el documento fiscal. -->
                  <button v-if="!s.deuda_cobrada" class="vx-btn vx-btn-sm"
                          :data-test="`btn-quitar-${s.cr_suscripcion_id}`"
                          :disabled="ocupado"
                          title="Quitarle este cargo: anula la deuda que se le emitió."
                          @click="abrirQuitar(s)">
                    <i class="bi bi-x-circle"></i> Quitar
                  </button>
                  <span v-else class="vx-ayuda">Ya pagado: se anula el cobro</span>
                </div>
                <span v-else-if="s.sin_responsable" class="vx-estado vx-estado-atencion">
                  Sin responsable
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Paso 4: ver antes de escribir -->
        <div class="d-flex justify-content-between align-items-center mt-3">
          <div class="vx-ayuda">
            {{ elegidas.size }} de {{ elegiblesLibres.length }} disponibles
          </div>
          <button class="vx-btn vx-btn-primario" data-test="btn-previsualizar"
                  :disabled="!elegidas.size || !fechaVencimiento || ocupado"
                  @click="previsualizar">
            Previsualizar
          </button>
        </div>

        <div v-if="propuesta" class="vx-subpanel mt-3" data-test="propuesta">
          <div class="vx-celda-principal mb-1">
            Se van a crear
            <strong data-test="propuesta-cantidad">{{ propuesta.resumen.cantidad }}</strong>
            cargos por
            <strong class="vx-num" data-test="propuesta-total">
              {{ formatearMonto(propuesta.resumen.monto_total, moneda) }}
            </strong>,
            con vencimiento el {{ fechaCorta(propuesta.fecha_vencimiento) }}.
          </div>
          <div v-if="propuesta.sin_responsable.length" class="vx-ayuda">
            {{ propuesta.sin_responsable.length }} quedan afuera por no tener responsable
            de pago vigente.
          </div>
          <div class="vx-ayuda">Todavía no se escribió nada.</div>
          <div class="d-flex gap-2 mt-2">
            <button class="vx-btn vx-btn-primario vx-btn-sm" data-test="btn-confirmar-emision"
                    :disabled="ocupado" @click="confirmar">
              Confirmar y emitir
            </button>
            <button class="vx-btn vx-btn-sm" data-test="btn-cancelar-emision"
                    @click="propuesta = null">
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </template>

    <ConceptoEdicionModal
      v-if="editando" :concepto="editando" :moneda="moneda"
      @cerrar="editando = null" @guardado="alGuardar"
    />
    <ConceptoEliminacionModal
      v-if="eliminando" :concepto="eliminando" :moneda="moneda"
      @cerrar="eliminando = null" @eliminado="alEliminar"
    />
  </div>
</template>

<script setup>
// Cargos eventuales — CE-F7. SPEC_CARGO_EVENTUAL_v0.1.md, D38.
//
// La pantalla sigue la forma de la generación de períodos —elegir, previsualizar,
// confirmar— a propósito: es la misma idea, y el operador no tiene que aprender
// dos. Lo que cambia es a QUIÉN: acá se eligen las suscripciones una por una,
// que es todo el punto de la iniciativa (CE-4).

import { ref, reactive, computed, onMounted } from 'vue'
import AppNavbar from '@/components/AppNavbar.vue'
import ConceptoAccionesMenu from '@/components/ConceptoAccionesMenu.vue'
import ConceptoEdicionModal from '@/components/ConceptoEdicionModal.vue'
import ConceptoEliminacionModal from '@/components/ConceptoEliminacionModal.vue'
import { mensajeEdicion, mensajeEliminacion, mensajeReactivacion } from '@/utils/conceptoAcciones.mjs'
import { useNotificationStore } from '@/stores/notifications'
import {
  listarEsquemas, listarCargos, crearCargo, desactivarCargo, reactivarConceptoPorTipo,
  elegiblesParaCargo, previsualizarCargo, emitirCargo, anularCargoEmitido,
  TIPOS_IVA, ivaTexto, formatearMonto, fechaCorta
} from '@/services/cobranzaService'
import { fechaIsoLocal } from '@/utils/fechaLocal.mjs'

const notificaciones = useNotificationStore()

const esquemas = ref([])
const esquemaId = ref(null)
const cargos = ref([])
const cargoId = ref(null)
const elegibles = ref([])
const elegidas = ref(new Set())
const propuesta = ref(null)
const mostrarAlta = ref(false)
const guardando = ref(false)
const ocupado = ref(false)
const fechaVencimiento = ref('')

const nuevo = reactive({ descripcion: '', monto: null, tipo_iva: 1 })

// D45: el cargo sobre el que está abierto cada modal. Son los mismos modales
// que la pantalla del esquema; la puerta (`/api/cobranza/cargos`) la elige el
// servicio por el tipo, así que el gerente los usa sin el permiso de esquemas.
const editando = ref(null)
const eliminando = ref(null)

// La emision que se esta deshaciendo, y su motivo.
const quitando = ref(null)
const motivoQuitar = ref('')

const hoy = fechaIsoLocal()

const cargoActual = computed(() =>
  cargos.value.find((c) => c.cr_esquema_concepto_id === cargoId.value) || null)

const moneda = computed(() =>
  esquemas.value.find((e) => e.cr_esquema_id === esquemaId.value)?.moneda || 'PYG')

// Los que se pueden elegir de verdad. Se muestran todos (arriba), pero el
// contador no puede mentir diciendo «3 de 40» cuando 38 ya lo tienen.
const elegiblesLibres = computed(() =>
  elegibles.value.filter((s) => !s.ya_lo_tiene && !s.sin_responsable))

// El store expone `notify({ type, message })` — y sólo eso. No tiene `success()`
// ni `error()`: llamarlos lanza `TypeError` y corta el flujo ANTES de recargar
// la tabla, con la pantalla en blanco y sin decir por qué. Lo delató el E2E, que
// es la única capa que ejecuta este código.
const avisar = (err, caida) => {
  notificaciones.notify({
    type: 'error',
    message: err?.userMessage || err?.response?.data?.message || caida
  })
}

const avisarOk = (message) => notificaciones.notify({ type: 'success', message })

onMounted(async () => {
  try {
    esquemas.value = await listarEsquemas({ activo: true })
  } catch (err) { avisar(err, 'No se pudieron cargar los esquemas.') }
})

// Sólo la lista de cargos. **No toca la selección**, y eso importa: al
// confirmar una emisión hay que refrescar los contadores de «Emitidos» sin
// cerrarle el panel al operador, que probablemente quiera emitirle al siguiente.
async function cargarCargos () {
  if (!esquemaId.value) return
  try {
    cargos.value = await listarCargos(esquemaId.value)
  } catch (err) { avisar(err, 'No se pudieron cargar los cargos.') }
}

// Cambiar de esquema SÍ descarta todo: los cargos, el padrón y la propuesta son
// de otro esquema y dejarlos en pantalla sería mostrar datos de algo que ya no
// se está mirando.
async function cambiarEsquema () {
  cargoId.value = null
  propuesta.value = null
  elegibles.value = []
  cargos.value = []
  await cargarCargos()
}

async function guardar () {
  guardando.value = true
  try {
    await crearCargo({ cr_esquema_id: esquemaId.value, ...nuevo })
    Object.assign(nuevo, { descripcion: '', monto: null, tipo_iva: 1 })
    mostrarAlta.value = false
    avisarOk('Cargo creado. Todavía no se le emitió a nadie.')
    await cargarCargos()
  } catch (err) {
    avisar(err, 'No se pudo crear el cargo.')
  } finally { guardando.value = false }
}

function alElegirAccion (accion, cargo) {
  if (accion === 'editar') editando.value = cargo
  else if (accion === 'eliminar') eliminando.value = cargo
  else if (accion === 'desactivar') desactivar(cargo)
  else if (accion === 'reactivar') reactivar(cargo)
}

// RN-181. `await`: el confirm de la app devuelve una promesa (F-2).
async function reactivar (cargo) {
  const ok = await window.confirm(mensajeReactivacion(cargo), {
    title: '¿Reactivar el cargo?', confirmText: 'Sí, reactivar', cancelText: 'Cancelar'
  })
  if (!ok) return
  try {
    await reactivarConceptoPorTipo(cargo)
    avisarOk('Cargo reactivado: vuelve a estar disponible para emitir.')
    await cargarCargos()
  } catch (err) {
    avisar(err, 'No se pudo reactivar el cargo.')
  }
}

// Si el panel de emisión era de este cargo, se cierra: emitir un cargo
// desactivado o borrado es justo lo que se acaba de impedir.
function soltarSiEsElAbierto (cargo) {
  if (cargoId.value === cargo.cr_esquema_concepto_id) {
    cargoId.value = null
    propuesta.value = null
    elegibles.value = []
  }
}

async function alGuardar (r) {
  editando.value = null
  avisarOk(mensajeEdicion(r))
  await cargarCargos()
}

async function alEliminar (r) {
  soltarSiEsElAbierto(eliminando.value)
  eliminando.value = null
  avisarOk(mensajeEliminacion(r))
  await cargarCargos()
}

async function desactivar (cargo) {
  // Desactivar no borra ni toca lo emitido, pero HOY no hay camino de vuelta
  // por pantalla: el modelo solo sabe poner `activo = FALSE`. Por eso se
  // pregunta antes.
  //
  // **`await` no es opcional.** `main.js` reemplaza `window.confirm` por el
  // diálogo propio (`AppConfirmDialog`), que devuelve una PROMESA. Sin `await`
  // la promesa es «verdadera» siempre: el cargo se desactivaba aunque el
  // operador eligiera Cancelar, y el diálogo quedaba abierto encima. Lo
  // destapó el E2E de D45, que es el primero que hace algo después.
  const sigue = await window.confirm(
    `Desactivar «${cargo.descripcion}»?\n\n`
    + 'Deja de ofrecerse para emitir. Lo que ya se emitio sigue en pie y se '
    + 'sigue cobrando.')
  if (!sigue) return

  guardando.value = true
  try {
    await desactivarCargo(cargo.cr_esquema_concepto_id)
    // Si estaba abierto el panel de emision de ESTE cargo, se cierra: emitir un
    // cargo desactivado es justo lo que se acaba de impedir.
    soltarSiEsElAbierto(cargo)
    avisarOk('Cargo desactivado. Lo ya emitido sigue en pie.')
    await cargarCargos()
  } catch (err) {
    avisar(err, 'No se pudo desactivar el cargo.')
  } finally { guardando.value = false }
}

function abrirQuitar (suscripcion) {
  quitando.value = {
    cr_deuda_id: suscripcion.cr_deuda_id,
    nombre: suscripcion.ficha?.cabecera?.nombre || `Suscripcion ${suscripcion.cr_suscripcion_id}`
  }
  motivoQuitar.value = ''
}

// La operacion inversa de emitir — RN-65.
async function confirmarQuitar () {
  ocupado.value = true
  try {
    await anularCargoEmitido(quitando.value.cr_deuda_id, motivoQuitar.value.trim())
    const nombre = quitando.value.nombre
    quitando.value = null
    motivoQuitar.value = ''
    avisarOk(`Se le quito el cargo a ${nombre}.`)
    // Las dos lecturas: el contador de «Emitidos» bajo, y esa fila del padron
    // vuelve a ser elegible (RN-62 mira `estado <> 'ANULADA'`).
    await cargarCargos()
    await abrirEmision({ cr_esquema_concepto_id: cargoId.value })
  } catch (err) {
    avisar(err, 'No se pudo quitar el cargo.')
  } finally { ocupado.value = false }
}

async function abrirEmision (cargo) {
  cargoId.value = cargo.cr_esquema_concepto_id
  propuesta.value = null
  elegidas.value = new Set()
  // Un panel de «quitar» a medio llenar es de la emision que se estaba
  // mirando. Cambiar de cargo sin cerrarlo lo dejaria apuntando a otra deuda.
  quitando.value = null
  try {
    const r = await elegiblesParaCargo(cargo.cr_esquema_concepto_id)
    elegibles.value = r.suscripciones || []
  } catch (err) { avisar(err, 'No se pudo cargar el padrón.') }
}

function alternar (s) {
  const proximas = new Set(elegidas.value)
  if (proximas.has(s.cr_suscripcion_id)) proximas.delete(s.cr_suscripcion_id)
  else proximas.add(s.cr_suscripcion_id)
  elegidas.value = proximas
  // Lo elegido cambió: lo que se previsualizó ya no describe lo que se va a
  // hacer. Se tira, en vez de dejar en pantalla un número que miente.
  propuesta.value = null
}

function marcarTodos (todos) {
  elegidas.value = todos
    ? new Set(elegiblesLibres.value.map((s) => s.cr_suscripcion_id))
    : new Set()
  propuesta.value = null
}

async function previsualizar () {
  ocupado.value = true
  try {
    propuesta.value = await previsualizarCargo(cargoId.value, {
      suscripcion_ids: [...elegidas.value],
      fecha_vencimiento: fechaVencimiento.value
    })
  } catch (err) {
    avisar(err, 'No se pudo previsualizar la emisión.')
  } finally { ocupado.value = false }
}

async function confirmar () {
  ocupado.value = true
  try {
    const r = await emitirCargo(cargoId.value, {
      suscripcion_ids: [...elegidas.value],
      fecha_vencimiento: fechaVencimiento.value
    })
    const omitidas = r.omitidas.length
      ? ` (${r.omitidas.length} ya lo tenían)` : ''
    avisarOk(
      `Se emitieron ${r.creadas.length} cargos por `
      + `${formatearMonto(r.resumen.monto_total, moneda.value)}${omitidas}.`)
    propuesta.value = null
    elegidas.value = new Set()
    // Refresca los contadores de «Emitidos» y vuelve a leer el padrón, que
    // ahora tiene marcados a los que acaban de recibirlo (RN-62).
    await cargarCargos()
    await abrirEmision({ cr_esquema_concepto_id: cargoId.value })
  } catch (err) {
    avisar(err, 'No se pudo emitir el cargo.')
  } finally { ocupado.value = false }
}
</script>
